import React from "react";
import { motion } from "framer-motion";
import { FaYoutube, FaLinkedin } from "react-icons/fa";
import Image from "next/image";

// Define types for better type safety and documentation
type CompanyLogo = {
  name: string;
  logo: React.ReactNode;
  href?: string;
};

// Company logos section - Updated for videographer portfolio
const COMPANY_LOGOS: CompanyLogo[] = [
  {
    name: "MELO",
    logo: (
      <div className="flex items-center justify-center">
        <Image 
          src="/logo.png" 
          alt="MELO Logo" 
          width={120} 
          height={60} 
          className="h-12 w-auto object-contain"
        />
      </div>
    ),
    href: "https://melo.cool"
  },
  {
    name: "LlamaIndex",
    logo: (
      <div className="flex items-center justify-center">
        <Image 
          src="/logos/LlamaIndex.png" 
          alt="LlamaIndex Logo" 
          width={120} 
          height={60} 
          className="h-12 w-auto object-contain"
        />
      </div>
    ),
    href: "https://llamaindex.ai"
  },
  {
    name: "Y Combinator Startups",
    logo: (
      <div className="flex items-center justify-center">
        <Image 
          src="/logos/Y_Combinator.svg" 
          alt="Y Combinator Logo" 
          width={120} 
          height={60} 
          className="h-12 w-auto object-contain"
        />
      </div>
    ),
    href: "https://ycombinator.com"
  },

  {
    name: "OpenAI",
    logo: (
      <div className="flex items-center justify-center">
        <Image 
          src="/logos/openai.jpg.webp" 
          alt="OpenAI Logo" 
          width={560} 
          height={280} 
          className="h-64 w-auto object-contain"
        />
      </div>
    ),
    href: "https://openai.com"
  },
  {
    name: "Rakuten",
    logo: (
      <div className="flex items-center justify-center">
        <Image 
          src="/logos/Rakuten.png" 
          alt="Rakuten Logo" 
          width={120} 
          height={60} 
          className="h-12 w-auto object-contain"
        />
      </div>
    ),
    href: "https://rakuten.com"
  },
  {
    name: "Flourish",
    logo: (
      <div className="flex items-center justify-center">
        <Image 
          src="/logos/Flourish.png" 
          alt="Flourish Logo" 
          width={120} 
          height={60} 
          className="h-12 w-auto object-contain"
        />
      </div>
    ),
    href: "https://flourishventures.com"
  }
];

// Social media links
const SOCIAL_LINKS = [
  { 
    href: "https://www.youtube.com/channel/UChnacOBuRIVesWW6KnjjG6w", 
    label: "YouTube", 
    icon: <FaYoutube className="w-8 h-8 text-gray-400" /> 
  },
  { 
    href: "https://www.linkedin.com/in/chloe-levalley/", 
    label: "LinkedIn", 
    icon: <FaLinkedin className="w-8 h-8 text-gray-400" /> 
  },
  { 
    href: "https://melo.cool", 
    label: "MELO", 
    icon: <Image src="/logo.png" alt="MELO Logo" width={32} height={32} className="w-8 h-8 object-contain" /> 
  },
] as const;

const Footer = () => {
  return (
          <footer className="bg-black">
      <div className="max-w-6xl mx-auto px-6 pt-0 pb-20">

        
        {/* Clients Section */}
        <motion.div 
          className="mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="text-center mb-12">
            <h3 className="text-2xl font-bold text-white mb-4">TRUSTED BY</h3>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center">
            {COMPANY_LOGOS.map((company, index) => (
              <motion.div
                key={company.name}
                className="flex items-center justify-center group"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                {company.href ? (
                  <motion.a
                    href={company.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group-hover:opacity-80 transition-opacity duration-300"
                  >
                    {company.logo}
                  </motion.a>
                ) : (
                  <div className="group-hover:opacity-80 transition-opacity duration-300">
                    {company.logo}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Contact Information and Social Links */}
        <motion.div 
          className="text-center space-y-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >

          
          {/* Contact Methods */}
          <div className="flex justify-center items-center">
            <motion.a
              href="mailto:chloe@melo.cool"
              className="text-white text-lg font-medium hover:text-gray-300 transition-colors duration-300 flex items-center space-x-2"
              whileHover={{ scale: 1.05 }}
            >
              <span>chloe@melo.cool</span>
            </motion.a>
          </div>
          
          {/* Social Icons */}
          <div className="flex justify-center items-center space-x-6">
            {SOCIAL_LINKS.map((social, index) => (
              <motion.a
                key={social.href}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="
                  text-gray-400
                  hover:text-white 
                  transition-all duration-300 
                  p-5
                  hover:bg-gray-800/50
                "
                aria-label={social.label}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.1, y: -2 }}
              >
                {social.icon}
              </motion.a>
            ))}
          </div>


        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
